import { useCallback, useState } from "react"
import { Dayjs } from "utils/dayjs"

import { OutgoingInvoiceCreditType, OutgoingInvoiceType, ReservationOutgoingInvoices } from "models/OutgoingInvoice"
import AssetService from "services/AssetService"
import { Guid } from "@/types"
import ReservationAsset from "models/ReservationAsset"


export enum EndDateError {
    invoices = 'invoices',
    assets = 'assets',
    system = 'system',
}

export default function useEarlyReturnEndDateValidation() {
    const [invoicesCache, setInvoicesCache] = useState<ReservationOutgoingInvoices | null>(null)

    const validateEndDate = useCallback(async ({
        reservationId,
        reservationAssets,
        latestNotCanceledInvoiceEnd,
        endDate,
    }: {
        reservationId: Guid
        reservationAssets: ReservationAsset[]
        latestNotCanceledInvoiceEnd: Dayjs | null
        endDate: Dayjs
    }): Promise<EndDateError[]> => {
        const errors: Set<EndDateError> = new Set()

        // Invoices
        if (latestNotCanceledInvoiceEnd && endDate.isBefore(latestNotCanceledInvoiceEnd)) {
            let invoices: ReservationOutgoingInvoices | null = null

            if (invoicesCache) {
                invoices = invoicesCache
            } else {
                const invoicesResp = await AssetService.getReservationInvoices(reservationId)
                invoices = invoicesResp
                setInvoicesCache(invoicesResp)
            }

            if (!invoices) {
                console.error("Could not fetch invoices")
                errors.add(EndDateError.system)
            }

            if (!invoices.linkCreateCollectiveCreditNote) {
                console.error('Collective credit note creation is not allowed')
                errors.add(EndDateError.invoices)
            }

            const invoiceAroundEndDate = invoices.invoices.find(inv => inv.type === OutgoingInvoiceType.invoice
                && inv.creditType !== OutgoingInvoiceCreditType.canceled
                && inv.start.isSameOrBefore(endDate)
                && (!inv.end || inv.end.isSameOrAfter(endDate))
            )

            if (invoiceAroundEndDate) {
                if (!invoiceAroundEndDate.collectivelyCreditable) {
                    console.error('Invoice cannot be canceled', invoiceAroundEndDate)
                    errors.add(EndDateError.invoices)
                }
            }

        }

        // Assets
        const hasAssetsStartAfterEarlyReturnEndDate = reservationAssets.some(asset => asset.start && asset.start.isAfter(endDate))
        if (hasAssetsStartAfterEarlyReturnEndDate) {
            console.error('Has assets after end date')
            errors.add(EndDateError.assets)
        }

        return [...errors]
    }, [invoicesCache])

    return {
        validateEndDate,
    }

}
